import React, { useState, useRef, useEffect } from 'react';
import { Form, Button, ListGroup, Row, Col, Alert, Badge, Spinner } from 'react-bootstrap';
import { FaFileUpload, FaExclamationTriangle, FaCheck, FaTimesCircle, FaCloudUploadAlt, FaInfoCircle, FaCloud } from 'react-icons/fa';
import PropTypes from 'prop-types';
import { getContactFiles } from '../../api/hubspotApi';

// Custom styles for form labels
const labelStyle = {
  fontWeight: '600',
  color: '#495057',
  marginBottom: '0.25rem',
  fontSize: '0.95rem'
};

// Styles for the drop zone
const dropZoneStyle = {
  border: '2px dashed #ccc',
  borderRadius: '8px',
  padding: '20px',
  textAlign: 'center',
  cursor: 'pointer',
  backgroundColor: '#f8f9fa',
  transition: 'all 0.3s ease'
};

const activeDropZoneStyle = {
  ...dropZoneStyle,
  border: '2px dashed #0d6efd',
  backgroundColor: 'rgba(13, 110, 253, 0.05)'
};

const successDropZoneStyle = {
  ...dropZoneStyle,
  border: '2px dashed #198754',
  backgroundColor: 'rgba(25, 135, 84, 0.05)'
};

/**
 * FileUploads Component
 * 
 * Handles file uploads for bank statements and application documents.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.bankFiles - Array of bank statement files
 * @param {Function} props.setBankFiles - Function to update bank files
 * @param {Array} props.appFiles - Array of application files
 * @param {Function} props.setAppFiles - Function to update app files
 * @param {boolean} props.disabled - Whether upload is disabled
 * @param {boolean} props.submitting - Whether submission is in progress
 * @returns {JSX.Element} FileUploads component
 */
const FileUploads = ({ 
  bankFiles = [], 
  setBankFiles, 
  appFiles = [], 
  setAppFiles, 
  disabled = false, 
  submitting = false,
  contactId = null,
  enableHubspotFiles = false,
  preloadedHubspotFiles = [],
  hubspotFilesLoading = false
}) => {
  const [activeDragZone, setActiveDragZone] = useState(null);
  const [fileSource, setFileSource] = useState('hubspot'); // Default to HubSpot files
  const [hubspotFiles, setHubspotFiles] = useState([]);
  const [loadingHubspotFiles, setLoadingHubspotFiles] = useState(false);
  const bankInputRef = useRef(null);
  const appInputRef = useRef(null);
  
  // Use preloaded files if available
  useEffect(() => {
    if (preloadedHubspotFiles && preloadedHubspotFiles.length > 0) {
      console.log(`[FileUploads] Using ${preloadedHubspotFiles.length} pre-loaded HubSpot files`);
      setHubspotFiles(preloadedHubspotFiles);
      setLoadingHubspotFiles(false);
    } else if (hubspotFilesLoading) {
      setLoadingHubspotFiles(true);
    }
  }, [preloadedHubspotFiles, hubspotFilesLoading]);

  /**
   * Handle file selection for bank statements or application documents
   * 
   * @param {Event} e - Change event from file input
   * @param {string} fileType - Type of file ('bank' or 'app')
   */
  const handleFileChange = (e, fileType) => {
    const files = Array.from(e.target.files);
    addFiles(files, fileType);
  };

  /**
   * Add files to the appropriate collection
   * 
   * @param {Array} files - Files to add
   * @param {string} fileType - Type of file ('bank' or 'app')
   */
  const addFiles = (files, fileType) => {
    if (disabled || submitting) return;
    
    if (fileType === 'bank') {
      setBankFiles(prevFiles => [...prevFiles, ...files]);
    } else if (fileType === 'app') {
      setAppFiles(prevFiles => [...prevFiles, ...files]);
    }
  };

  /**
   * Remove a file from the selected files list
   * 
   * @param {string} fileType - Type of file ('bank' or 'app')
   * @param {number} index - Index of the file to remove
   */
  const handleRemoveFile = (fileType, index) => {
    if (fileType === 'bank') {
      setBankFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    } else if (fileType === 'app') {
      setAppFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    }
  };

  /**
   * Handle drag over event for drop zone
   * 
   * @param {Event} e - Drag event
   * @param {string} fileType - Type of file ('bank' or 'app')
   */
  const handleDragOver = (e, fileType) => {
    e.preventDefault();
    if (disabled || submitting) return;
    setActiveDragZone(fileType);
  };

  /**
   * Handle drag leave event for drop zone
   * 
   * @param {Event} e - Drag event
   */
  const handleDragLeave = (e) => {
    e.preventDefault();
    setActiveDragZone(null);
  };

  /**
   * Handle drop event for drop zone
   * 
   * @param {Event} e - Drop event
   * @param {string} fileType - Type of file ('bank' or 'app')
   */
  const handleDrop = (e, fileType) => {
    e.preventDefault();
    if (disabled || submitting) return;
    
    setActiveDragZone(null);
    
    // Check if files are dropped
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      addFiles(files, fileType);
    }
  };

  /**
   * Handle click on the drop zone to open file browser
   * 
   * @param {string} fileType - Type of file ('bank' or 'app')
   */
  const handleDropZoneClick = (fileType) => {
    if (disabled || submitting) return;
    
    if (fileType === 'bank' && bankInputRef.current) {
      bankInputRef.current.click();
    } else if (fileType === 'app' && appInputRef.current) {
      appInputRef.current.click();
    }
  };

  /**
   * Handle file source change (manual vs HubSpot)
   * 
   * @param {string} source - File source ('manual' or 'hubspot')
   */
  const handleFileSourceChange = (source) => {
    setFileSource(source);
    // Files are already pre-loaded in background, no need to fetch
    console.log(`[FileUploads] Switched to ${source} file source`);
  };

  /**
   * Categorize a HubSpot file as bank or app
   * 
   * @param {Object} file - HubSpot file object
   * @param {string} category - 'bank' or 'app'
   */
  const handleCategorizeFile = (file, category) => {
    // Convert HubSpot file to File-like object structure
    const fileObj = { 
      id: file.id, 
      name: file.name, 
      size: file.size, 
      url: file.url,
      extension: file.extension,
      isHubspotFile: true 
    };
    
    if (category === 'bank') {
      // Remove from app if exists, add to bank
      setAppFiles(prev => prev.filter(f => f.id !== file.id));
      if (!bankFiles.find(f => f.id === file.id)) {
        setBankFiles(prev => [...prev, fileObj]);
      }
    } else {
      // Remove from bank if exists, add to app
      setBankFiles(prev => prev.filter(f => f.id !== file.id));
      if (!appFiles.find(f => f.id === file.id)) {
        setAppFiles(prev => [...prev, fileObj]);
      }
    }
  };

  /**
   * Remove a HubSpot file from selection
   * 
   * @param {string} fileId - HubSpot file ID
   * @param {string} category - 'bank' or 'app'
   */
  const handleRemoveHubSpotFile = (fileId, category) => {
    if (category === 'bank') {
      setBankFiles(prev => prev.filter(f => f.id !== fileId));
    } else {
      setAppFiles(prev => prev.filter(f => f.id !== fileId));
    }
  };

  /**
   * Check if file is selected as bank statement
   * 
   * @param {string} fileId - File ID
   * @returns {boolean}
   */
  const isFileSelectedAsBank = (fileId) => {
    return bankFiles.some(f => f.id === fileId);
  };

  /**
   * Check if file is selected as app document
   * 
   * @param {string} fileId - File ID
   * @returns {boolean}
   */
  const isFileSelectedAsApp = (fileId) => {
    return appFiles.some(f => f.id === fileId);
  };

  /**
   * Format file size for display
   * 
   * @param {number} bytes - File size in bytes
   * @returns {string} Formatted file size
   */
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Note: HubSpot files are now pre-loaded in ContactDetail.js
  // No need to fetch on mount - files are already available via preloadedHubspotFiles prop

  const bankUploaded = bankFiles.length > 0;
  const appUploaded = appFiles.length > 0;
  // Only bank statements are required, app files are optional
  const isFileRequirementMet = bankUploaded;
  const totalUploaded = (bankUploaded ? 1 : 0) + (appUploaded ? 1 : 0);

  // Determine drop zone style based on state
  const getBankDropZoneStyle = () => {
    if (bankUploaded) return successDropZoneStyle;
    if (activeDragZone === 'bank') return activeDropZoneStyle;
    return dropZoneStyle;
  };

  const getAppDropZoneStyle = () => {
    if (appUploaded) return successDropZoneStyle;
    if (activeDragZone === 'app') return activeDropZoneStyle;
    return dropZoneStyle;
  };

  return (
    <div className="file-uploads-container">
      {/* File Source Selector */}
      {enableHubspotFiles && contactId && (
        <div className="mb-3">
          <Form.Check
            type="radio"
            id="file-source-hubspot"
            name="fileSource"
            label={
              <span>
                <FaCloud className="me-2" />
                HubSpot Files
              </span>
            }
            checked={fileSource === 'hubspot'}
            onChange={() => handleFileSourceChange('hubspot')}
            disabled={submitting || disabled}
            inline
            className="me-4"
          />
          <Form.Check
            type="radio"
            id="file-source-manual"
            name="fileSource"
            label={
              <span>
                <FaFileUpload className="me-2" />
                Upload Files Manually
              </span>
            }
            checked={fileSource === 'manual'}
            onChange={() => handleFileSourceChange('manual')}
            disabled={submitting || disabled}
            inline
          />
        </div>
      )}

      {!isFileRequirementMet && (
        <Alert variant="warning" className="mb-3">
          <FaExclamationTriangle className="me-2" />
          <strong>Bank statements are required for lender submission.</strong> Please upload at least one bank statement.
        </Alert>
      )}
      
      {isFileRequirementMet && (
        <Alert variant="success" className="mb-3">
          <FaCheck className="me-2" />
          <strong>All required files uploaded!</strong> You can continue to the next section or add more files if needed.
        </Alert>
      )}

      {/* HubSpot File Selection UI */}
      {fileSource === 'hubspot' && enableHubspotFiles && contactId ? (
        <>
          <Alert variant="info" className="mb-3">
            <FaInfoCircle className="me-2" />
            Select files from HubSpot and categorize them as Bank Statements or Application Documents.
          </Alert>
          
          <div className="mb-4" style={{ maxWidth: '900px', fontSize: '0.875rem' }}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0 fw-bold" style={{ fontSize: '0.9rem' }}>Available HubSpot Files</h6>
              <div>
                <Badge bg={bankFiles.length > 0 ? 'success' : 'danger'} className="me-2" style={{ fontSize: '0.7rem' }}>
                  Banks: {bankFiles.length}
                </Badge>
                <Badge bg="secondary" style={{ fontSize: '0.7rem' }}>
                  Apps: {appFiles.length}
                </Badge>
              </div>
            </div>
            
            {loadingHubspotFiles ? (
              <div className="text-center py-3">
                <Spinner animation="border" variant="primary" size="sm" />
                <p className="mt-2 mb-0 text-muted" style={{ fontSize: '0.8rem' }}>Loading files from HubSpot...</p>
              </div>
            ) : hubspotFiles.length === 0 ? (
              <Alert variant="warning" className="mb-0" style={{ fontSize: '0.85rem' }}>
                <FaExclamationTriangle className="me-2" />
                No files found in HubSpot for this contact.
              </Alert>
            ) : (
              <div className="border rounded">
                {hubspotFiles.map((file, index) => (
                  <div 
                    key={file.id} 
                    className={`d-flex justify-content-between align-items-center px-3 py-2 ${index !== hubspotFiles.length - 1 ? 'border-bottom' : ''}`}
                  >
                    <div className="flex-grow-1 me-3">
                      <div className="fw-medium">
                        {file.name}{file.extension && !file.name.toLowerCase().endsWith(`.${file.extension.toLowerCase()}`) ? `.${file.extension}` : ''}
                      </div>
                      <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                        {file.extension && <Badge bg="secondary" className="me-2" style={{ fontSize: '0.65rem' }}>{file.extension.toUpperCase()}</Badge>}
                        {formatFileSize(file.size)} • {file.createdAt ? new Date(file.createdAt).toLocaleDateString() : 'Unknown date'}
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <Button
                        size="sm"
                        variant={isFileSelectedAsBank(file.id) ? 'success' : 'outline-secondary'}
                        onClick={() => handleCategorizeFile(file, 'bank')}
                        disabled={submitting || disabled}
                        style={{ minWidth: '60px', fontSize: '0.8rem' }}
                      >
                        {isFileSelectedAsBank(file.id) && <FaCheck className="me-1" />}
                        Bank
                      </Button>
                      <Button
                        size="sm"
                        variant={isFileSelectedAsApp(file.id) ? 'success' : 'outline-secondary'}
                        onClick={() => handleCategorizeFile(file, 'app')}
                        disabled={submitting || disabled}
                        style={{ minWidth: '60px', fontSize: '0.8rem' }}
                      >
                        {isFileSelectedAsApp(file.id) && <FaCheck className="me-1" />}
                        App
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Selected files sections */}
          <div style={{ maxWidth: '900px' }}>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <h6 className="mb-2 fw-bold" style={{ fontSize: '0.9rem' }}>
                    Bank Statements 
                    <Badge bg={bankFiles.length > 0 ? 'success' : 'secondary'} className="ms-2" style={{ fontSize: '0.7rem' }}>
                      {bankFiles.length}
                    </Badge>
                  </h6>
                  {bankFiles.length === 0 ? (
                    <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>No bank statements selected</p>
                  ) : (
                    <div className="border rounded">
                      {bankFiles.map((file, index) => (
                        <div 
                          key={file.id} 
                          className={`d-flex justify-content-between align-items-center px-3 py-2 ${index !== bankFiles.length - 1 ? 'border-bottom' : ''}`}
                          style={{ fontSize: '0.875rem' }}
                        >
                          <span className="text-truncate">
                            {file.isHubspotFile ? (
                              <>
                                {file.name}{file.extension && !file.name.toLowerCase().endsWith(`.${file.extension.toLowerCase()}`) ? `.${file.extension}` : ''}
                              </>
                            ) : (
                              file.name
                            )}
                          </span>
                          <Button
                            variant="link"
                            size="sm"
                            className="text-danger p-0 ms-2"
                            onClick={() => handleRemoveHubSpotFile(file.id, 'bank')}
                            disabled={submitting}
                            style={{ fontSize: '0.8rem' }}
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <h6 className="mb-2 fw-bold" style={{ fontSize: '0.9rem' }}>
                    Application Documents 
                    <Badge bg={appFiles.length > 0 ? 'success' : 'secondary'} className="ms-2" style={{ fontSize: '0.7rem' }}>
                      {appFiles.length}
                    </Badge>
                  </h6>
                  {appFiles.length === 0 ? (
                    <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>No application documents selected</p>
                  ) : (
                    <div className="border rounded">
                      {appFiles.map((file, index) => (
                        <div 
                          key={file.id} 
                          className={`d-flex justify-content-between align-items-center px-3 py-2 ${index !== bankFiles.length - 1 ? 'border-bottom' : ''}`}
                          style={{ fontSize: '0.875rem' }}
                        >
                          <span className="text-truncate">
                            {file.isHubspotFile ? (
                              <>
                                {file.name}{file.extension && !file.name.toLowerCase().endsWith(`.${file.extension.toLowerCase()}`) ? `.${file.extension}` : ''}
                              </>
                            ) : (
                              file.name
                            )}
                          </span>
                          <Button
                            variant="link"
                            size="sm"
                            className="text-danger p-0 ms-2"
                            onClick={() => handleRemoveHubSpotFile(file.id, 'app')}
                            disabled={submitting}
                            style={{ fontSize: '0.8rem' }}
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </div>
        </>
      ) : (
        /* Manual Upload UI (existing code) */
        <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>
              Bank Statements <span className="text-danger">*</span>
              {bankUploaded ? (
                <Badge bg="success" className="ms-2">
                  <FaCheck className="me-1" />
                  Uploaded
                </Badge>
              ) : (
                <Badge bg="danger" className="ms-2">
                  <FaTimesCircle className="me-1" />
                  Required
                </Badge>
              )}
            </Form.Label>
            
            {/* Hidden file input */}
            <Form.Control
              ref={bankInputRef}
              type="file"
              multiple
              onChange={e => handleFileChange(e, 'bank')}
              disabled={submitting || disabled}
              className="d-none"
              id="bank-file-input"
            />
            
            {/* Drop zone */}
            <div
              style={getBankDropZoneStyle()}
              onDragOver={e => handleDragOver(e, 'bank')}
              onDragLeave={handleDragLeave}
              onDrop={e => handleDrop(e, 'bank')}
              onClick={() => handleDropZoneClick('bank')}
              className={`mb-3 file-drop-zone ${activeDragZone === 'bank' ? 'active' : ''}`}
            >
              <FaCloudUploadAlt size={32} className="mb-2" color={bankUploaded ? "#198754" : "#0d6efd"} />
              <div>
                <strong>{bankUploaded ? "Add More Bank Statements" : "Upload Bank Statements"}</strong>
              </div>
              <div className="text-muted">
                Drag & drop files here or click to browse
              </div>
            </div>
            
            {bankFiles.length > 0 && (
              <ListGroup variant="flush" className="mt-2 border-top border-bottom">
                {bankFiles.map((file, index) => (
                  <ListGroup.Item key={index} className="py-2 d-flex justify-content-between align-items-center">
                    <div className="text-truncate" style={{ maxWidth: '80%' }}>
                      {file.name}
                    </div>
                    <Button
                      variant="link"
                      size="sm"
                      className="text-danger p-0"
                      onClick={() => handleRemoveFile('bank', index)}
                      disabled={submitting}
                    >
                      Remove
                    </Button>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </Form.Group>
        </Col>
        
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>
              Application Documents <span className="text-muted">(Optional)</span>
              {appUploaded ? (
                <Badge bg="success" className="ms-2">
                  <FaCheck className="me-1" />
                  Uploaded
                </Badge>
              ) : (
                <Badge bg="secondary" className="ms-2">
                  <FaInfoCircle className="me-1" />
                  Optional
                </Badge>
              )}
            </Form.Label>
            
            {/* Hidden file input */}
            <Form.Control
              ref={appInputRef}
              type="file"
              multiple
              onChange={e => handleFileChange(e, 'app')}
              disabled={submitting || disabled}
              className="d-none"
              id="app-file-input"
            />
            
            {/* Drop zone */}
            <div
              style={getAppDropZoneStyle()}
              onDragOver={e => handleDragOver(e, 'app')}
              onDragLeave={handleDragLeave}
              onDrop={e => handleDrop(e, 'app')}
              onClick={() => handleDropZoneClick('app')}
              className={`mb-3 file-drop-zone ${activeDragZone === 'app' ? 'active' : ''}`}
            >
              <FaCloudUploadAlt size={32} className="mb-2" color={appUploaded ? "#198754" : "#0d6efd"} />
              <div>
                <strong>{appUploaded ? "Add More Application Documents" : "Upload Application Documents"}</strong>
              </div>
              <div className="text-muted">
                Drag & drop files here or click to browse
              </div>
            </div>
            
            {appFiles.length > 0 && (
              <ListGroup variant="flush" className="mt-2 border-top border-bottom">
                {appFiles.map((file, index) => (
                  <ListGroup.Item key={index} className="py-2 d-flex justify-content-between align-items-center">
                    <div className="text-truncate" style={{ maxWidth: '80%' }}>
                      {file.name}
                    </div>
                    <Button
                      variant="link"
                      size="sm"
                      className="text-danger p-0"
                      onClick={() => handleRemoveFile('app', index)}
                      disabled={submitting}
                    >
                      Remove
                    </Button>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </Form.Group>
        </Col>
      </Row>
      )}
    </div>
  );
};

FileUploads.propTypes = {
  bankFiles: PropTypes.array,
  setBankFiles: PropTypes.func.isRequired,
  appFiles: PropTypes.array,
  setAppFiles: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  submitting: PropTypes.bool,
  contactId: PropTypes.string,
  enableHubspotFiles: PropTypes.bool,
  preloadedHubspotFiles: PropTypes.array,
  hubspotFilesLoading: PropTypes.bool
};

export default FileUploads; 